/**
 * FAQSubmenu — 2단계 서브메뉴 그리드 카드
 *
 * "채널별 요금" 등 카테고리 하위에서 변형이 많은 항목을 그룹화.
 * 2열 그리드로 채널/옵션을 노출하고, 하단에 "이전 메뉴로" 버튼 제공.
 */

import { cn } from '@/lib/utils';
import type { FAQSubmenu, FAQSubmenuItem } from '@/types/qa';

interface FAQSubmenuProps {
  submenu: FAQSubmenu;
  onSelectIntent: (intent: string, label: string) => void;
  onBack: () => void;
}

export function FAQSubmenu({ submenu, onSelectIntent, onBack }: FAQSubmenuProps) {
  return (
    <div className="ml-flex ml-flex-col ml-gap-3 ml-mt-2">
      {submenu.groups.map((group) => (
        <div key={group.label} className="ml-flex ml-flex-col ml-gap-1.5">
          <div
            className={cn(
              'ml-text-cb-xs ml-font-semibold ml-text-mute',
              'ml-tracking-wider ml-uppercase ml-px-1'
            )}
          >
            {group.label}
          </div>
          <div className="ml-grid ml-grid-cols-2 ml-gap-2">
            {group.items.map((item) => (
              <ChannelButton
                key={item.id}
                item={item}
                onSelect={() => onSelectIntent(item.intent, item.label)}
              />
            ))}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={onBack}
        className={cn(
          'ml-self-start ml-flex ml-items-center ml-gap-1.5',
          'ml-px-3 ml-py-1.5 ml-rounded-cb-sm',
          'ml-text-cb-sm ml-text-mute ml-cursor-pointer',
          'hover:ml-bg-surface hover:ml-text-ink ml-transition-colors'
        )}
      >
        <span>←</span>
        <span>이전 메뉴로</span>
      </button>
    </div>
  );
}

interface ChannelButtonProps {
  item: FAQSubmenuItem;
  onSelect: () => void;
}

function ChannelButton({ item, onSelect }: ChannelButtonProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'ml-flex ml-flex-col ml-items-start ml-gap-1',
        'ml-p-3 ml-rounded-cb-md',
        'ml-bg-white ml-border ml-border-line',
        'ml-text-left ml-cursor-pointer',
        'ml-transition-all ml-duration-150',
        'hover:ml-border-brand-600 hover:ml-bg-brand-50',
        'hover:ml--translate-y-px'
      )}
    >
      <div className="ml-flex ml-items-center ml-gap-2 ml-w-full">
        {item.icon && (
          <span className="ml-text-base ml-leading-none">{item.icon}</span>
        )}
        <span className="ml-text-cb-base ml-font-semibold ml-text-ink ml-flex-1 ml-truncate">
          {item.label}
        </span>
      </div>
      {item.subtitle && (
        <span className="ml-text-cb-xs ml-text-mute ml-pl-6">{item.subtitle}</span>
      )}
    </button>
  );
}
